import 'ol/ol.css'
import './styles.css'
import { fetchMe, logout } from './api/auth'
import { ApiError, errorCode } from './api/client'
import { fetchLayerVersions } from './api/layerVersions'
import type { ApiMe, LayerVersions } from './api/types'
import { DASHBOARD_URL, INACTIVE_LIFECYCLE_STATUSES, PROXY_BASE } from './config'
import { fetchWorkspaceLayers, type LayerInfo } from './map/capabilities'
import { buildShareUrl, matchDeepLinkLayers, requestedLayerNames, requestedView } from './map/deepLink'
import { gate } from './map/enforcement'
import { unionExtent3857 } from './map/extent'
import { queryFeatures } from './map/featureInfo'
import {
  createWmsLayer,
  layerInfoOf,
  setEnforcementHandler,
  setTileSuccessHandler,
  type WmsTileLayer,
} from './map/layers'
import { stackOrder } from './map/layerNames'
import { applyVersions, startVersionRefresh, versionOf } from './map/layerVersions'
import { createBasemaps, createMap } from './map/mapInit'
import { createHighlight } from './map/highlight'
import { createVectorMode } from './map/vectorMode'
import { createLabelController } from './map/layerLabels'
import { loadViewState, saveViewState } from './map/viewState'
import { initTheme } from './ui/themeToggle'
import { createSettingsMenu } from './ui/settingsMenu'
import { hideAttributesDrawer, refreshAttributesDrawer } from './ui/attributesDrawer'
import { escapeHtml } from './ui/dom'
import { runExport } from './ui/exportButton'
import { renderGotoBar } from './ui/gotoBar'
import {
  hideSeatBanner,
  hideServiceToast,
  renderNotInPlan,
  renderSubscriptionInactive,
  resetServiceBackoff,
  showSeatBanner,
  showServiceToast,
} from './ui/errorStates'
import { renderLayerPanel } from './ui/layerPanel'
import { createLayoutShell } from './ui/layoutShell'
import { renderLoginScreen } from './ui/loginScreen'
import { renderMapToolbar } from './ui/mapToolbar'
import { createRightPanel } from './ui/rightPanel'
import { renderMeasureBar } from './ui/measureBar'
import { createDetailsPanel } from './ui/detailsPanel'
import { createSqlPanel } from './ui/sqlPanel'
import { addLogEntry } from './ui/debugLog'
import { installCrashGuard } from './ui/crashGuard'

const app = document.getElementById('app') as HTMLElement

// Crash net first, before anything else can throw during boot.
installCrashGuard(app)

// Debug activation via URL — replaces the console command as the primary entry
// point. Client-side only; grants no server privilege.
const debugParam = new URLSearchParams(location.search).get('debug')
if (debugParam === '1') localStorage.setItem('gv_debug', '1')
else if (debugParam === '0') localStorage.removeItem('gv_debug')

let currentMe: ApiMe | null = null
let mapReady = false
let disposeMap: (() => void) | null = null
let disposeSqlPanel: (() => void) | null = null
let disposeVersionRefresh: (() => void) | null = null
let screenGen = 0 // bumped by every teardown(), so an async screen build can tell it is stale
/** Longest the boot waits for /api/layer-versions before drawing the map without V. */
const VERSIONS_BOOT_WAIT_MS = 1500
let mapInstance: ReturnType<typeof createMap> | null = null
let wmsLayersRef: WmsTileLayer[] = []

// Fail-fast: a production bundle served from a real host with no proxy base
// configured would silently call its own origin instead of the API. Show a
// clear error instead of a broken map. Localhost/dev use same-origin on purpose.
//
// This runs *before* initTheme(): an application that refuses to start has no
// interface to theme, and the error card carries its own inline styles.
const _servedLocally = ['localhost', '127.0.0.1', ''].includes(location.hostname)
if (!PROXY_BASE && !_servedLocally) {
  app.innerHTML =
    '<div style="max-width:34rem;margin:4rem auto;padding:1.5rem;font-family:system-ui;' +
    'border:1px solid #d33;border-radius:8px">' +
    '<h1 style="font-size:1.15rem;margin:0 0 .5rem">Configuration error</h1>' +
    '<p style="margin:0;line-height:1.5">This GeoViewer build has no API proxy configured ' +
    '(<code>VITE_PROXY_BASE</code> was empty at build time), so it cannot reach the server. ' +
    'Rebuild with <code>VITE_PROXY_BASE=https://geo.digigeodata.com</code>.</p></div>'
  throw new Error(
    'VITE_PROXY_BASE was not set at build time — refusing to run against the wrong origin',
  )
}

initTheme()
void boot()

/**
 * Boot sequence (PRD P0.5): fetch /api/me first. A user already holding a
 * valid portal session (shared cross-subdomain cookie) skips the login screen
 * entirely; 401 shows the login form.
 */
async function boot(): Promise<void> {
  showLoading()
  let me: ApiMe
  try {
    me = await fetchMe()
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'auth') {
      showLogin()
      return
    }
    showBootError(err)
    return
  }
  route(me)
}

function route(me: ApiMe): void {
  currentMe = me
  if (!me.allows_map_viewer) {
    teardown()
    renderNotInPlan(app, () => void signOut(), errorCode('plan', 403))
    return
  }
  if (INACTIVE_LIFECYCLE_STATUSES.has(me.company.lifecycle_status)) {
    teardown()
    renderSubscriptionInactive(app, () => void signOut(), errorCode('subscription', 403))
    return
  }
  void startMapScreen(me)
}

async function startMapScreen(me: ApiMe): Promise<void> {
  teardown()
  const gen = screenGen // a later teardown() bumps it: this screen is then stale
  mapReady = false
  app.innerHTML = `
    <div class="app-shell">
      <header class="topbar">
        <div class="topbar__brand">GeoViewer</div>
        <div class="topbar__meta">
          <span class="badge">${escapeHtml(me.plan.name)}</span>
          <span class="topbar__email">${escapeHtml(me.email)}</span>
          ${me.allows_sql ? '<button class="btn btn--small" id="sql-open">Data Filter</button>' : ''}
          <button class="btn btn--small" id="share-view">Copy link</button>
          <button class="btn btn--small" id="panel-toggle">Layers</button>
          <a class="btn btn--small" id="my-account" href="${DASHBOARD_URL}" target="_blank" rel="noopener">My account</a>
          <span id="settings-slot"></span>
          <button class="btn btn--small" id="logout">Sign out</button>
        </div>
      </header>
    </div>`
  // The 3-zone layout grid (T1). Owns the panel/map/right/bottom slots and
  // keeps id="panel" and id="map", so the getElementById() calls below still
  // resolve. Replaces the former `.content` flex container.
  document.getElementById('settings-slot')!.appendChild(createSettingsMenu())
  const shell = createLayoutShell()
  document.querySelector('.app-shell')!.appendChild(shell.root)
  document.getElementById('logout')!.addEventListener('click', () => void signOut())
  document.getElementById('share-view')!.addEventListener('click', () => {
    if (!mapInstance) return
    const view = mapInstance.getView()
    const center = view.getCenter() ?? [0, 0]
    const zoom = view.getZoom() ?? 0
    const visible = wmsLayersRef.filter((l: WmsTileLayer) => l.getVisible()).map((l: WmsTileLayer) => layerInfoOf(l))
    const url = buildShareUrl(center, zoom, visible)
    void navigator.clipboard.writeText(url).then(() => {
      const btn = document.getElementById('share-view')!
      const orig = btn.textContent
      btn.textContent = 'Copied!'
      setTimeout(() => { btn.textContent = orig }, 1500)
    })
  })
  const panelEl = document.getElementById('panel') as HTMLElement
  // Mobile "Layers" button (≤820px) — the shell owns the drawer state so the
  // panel and the attribute table stay mutually exclusive on small screens.
  document.getElementById('panel-toggle')!.addEventListener('click', () => shell.toggleLeftDrawer())

  setEnforcementHandler(handleEnforcement)
  setTileSuccessHandler(resetServiceBackoff)

  // Layer discovery: filtered GetCapabilities per workspace (PRD P0.6) —
  // the layer list is exactly what the proxy advertises, nothing hardcoded.
  // Layer versions (tile freshness) are fetched alongside, but the map never
  // waits on them for long: fetchLayerVersions() never throws, and past
  // VERSIONS_BOOT_WAIT_MS the layers are created without `V`; the answer, when it
  // lands, goes through applyVersions() below.
  let infos: LayerInfo[]
  let versions: LayerVersions | null
  const versionsStartedAt = Date.now()
  const versionsCall = fetchLayerVersions()
  try {
    ;[infos, versions] = await Promise.all([
      Promise.all(me.workspaces.map((ws) => fetchWorkspaceLayers(ws))).then((l) => l.flat()),
      Promise.race([
        versionsCall,
        new Promise<null>((resolve) => setTimeout(resolve, VERSIONS_BOOT_WAIT_MS, null)),
      ]),
    ])
  } catch (err) {
    handleEnforcement(
      err instanceof ApiError ? err : new ApiError(0, 'unavailable', 'Cannot reach the map service.'),
    )
    return
  }

  const basemaps = createBasemaps()
  const map = createMap(document.getElementById('map') as HTMLElement, basemaps)
  // Re-fit the OpenLayers canvas whenever a layout zone is resized or the table
  // opens/closes — otherwise the map stays stretched (grey bands).
  shell.onResize(() => map.updateSize())
  const wmsLayers: WmsTileLayer[] = infos.map((info) => createWmsLayer(info, versions ? versionOf(info, versions) : undefined))
  mapInstance = map
  wmsLayersRef = wmsLayers

  // Initial visibility + extent precedence:
  // 1. share link (?lat/lon/zoom + ?layer=) — from "Copy link" button
  // 2. plain ?layer= deep link (P1.6) — pre-selects layers, fits their extent
  // 3. saved view state (P1.4) — restores last position
  // 4. fit advertised union extent
  const sharedView = requestedView(location.search)
  const linked = matchDeepLinkLayers(infos, requestedLayerNames(location.search))
  const saved = linked.length === 0 && !sharedView ? loadViewState() : null
  if (linked.length > 0) {
    const visible = new Set(linked)
    for (const layer of wmsLayers) layer.setVisible(visible.has(layerInfoOf(layer)))
  } else if (saved) {
    const visible = new Set(saved.layers)
    for (const layer of wmsLayers) layer.setVisible(visible.has(layerInfoOf(layer).name))
  }
  // Bottom first: layers of one group share a z-index, and OpenLayers breaks
  // the tie on insertion order — the panel's row order must hold on the map too.
  for (const layer of stackOrder(wmsLayers, (l) => layerInfoOf(l))) map.addLayer(layer)

  if (sharedView) {
    map.getView().setCenter(sharedView.center)
    map.getView().setZoom(sharedView.zoom)
  } else if (saved) {
    map.getView().setCenter(saved.center)
    map.getView().setZoom(saved.zoom)
  } else {
    const extent = unionExtent3857(linked.length > 0 ? linked : infos)
    if (extent) map.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 12 })
  }

  // Shapes, Attributes, and Labels are core viewer tools, so their controls are
  // present for every map-viewer plan. The proxy remains the enforcement point:
  // if a plan still refuses WFS, the controllers handle the 403 without granting
  // access client-side.
  const vector = createVectorMode(map, handleEnforcement)
  const labels = createLabelController(map)
  // Single highlight layer, shared by the click "Details" marker (T2), the
  // guided-SQL "Show on map" rectangle, and attribute-table rows (T3).
  const highlight = createHighlight(map)
  renderLayerPanel(panelEl, wmsLayers, basemaps, vector, labels, (extent) =>
    highlight.showBbox(extent as [number, number, number, number]),
  )
  // WFS features stream in by bbox: refresh the table as they land, instead of
  // leaving whatever snapshot existed when it was opened.
  vector?.onFeatures((wms) => refreshAttributesDrawer(layerInfoOf(wms).name))

  // Saved view state (P1.4): persist extent + row visibility on every change —
  // except during a deep-linked session (P1.6): a shared ?layer= URL carries
  // its own state and must not overwrite the user's remembered workspace.
  if (linked.length === 0) {
    const persistView = (): void => saveViewState(map, wmsLayers, vector)
    map.on('moveend', persistView)
    for (const layer of wmsLayers) layer.on('change:visible', persistView)
  }

  const mapEl = document.getElementById('map') as HTMLElement
  const toolbar = renderMapToolbar(mapEl)
  const measure = renderMeasureBar(
    toolbar.addTool('measure', 'Measure', 'Measure distance or area'),
    map,
  )
  renderGotoBar(toolbar.addTool('goto', 'Go to', 'Go to coordinates'), map)
  toolbar.addAction('export', 'Export PNG', 'Download the current view as a PNG', (button) =>
    runExport(button, map, basemaps),
  )
  // No active mode may be invisible (spec §3.4): folding the Measure tool away
  // puts the pencil down. Finished measurements stay drawn — `Clear` removes
  // those — but map clicks go back to GetFeatureInfo.
  toolbar.onToolClose('measure', () => measure.setMode(null))

  // GetFeatureInfo on click → right "Details" panel (T2), with a marker anchoring
  // the selection on the map. Closing the panel clears the marker.
  //
  // Each click gets a sequence token; closing the panel bumps it too. A response
  // is applied only if its token is still current — so a slow GetFeatureInfo that
  // resolves after the user closed the panel (or clicked elsewhere) is dropped
  // instead of popping the panel back open.
  let selectionSeq = 0
  const rightPanel = createRightPanel(shell)
  // Dismissing the panel drops the selection, whichever tab was up: the marker
  // on the map anchors a selection that is no longer displayed anywhere.
  rightPanel.onClose(() => {
    selectionSeq++
    highlight.clear()
  })
  const details = createDetailsPanel(rightPanel)

  // Guided SQL "Data Requests" (Phase 2): only when the plan allows it — the
  // button and the whole tab are absent otherwise, so a plan without the
  // feature shows a single-tab panel identical to before T5. Registered after
  // Details on purpose: tab order follows registration order.
  if (me.allows_sql) {
    const sqlPanel = createSqlPanel({
      mount: rightPanel.addTab('sql', 'Data Filter'),
      onShowBbox: (bbox) => highlight.showBbox(bbox),
      onEnforcement: handleEnforcement,
    })
    document.getElementById('sql-open')!.addEventListener('click', () => {
      rightPanel.open('sql')
      sqlPanel.ensureLoaded()
    })
    disposeSqlPanel = () => sqlPanel.dispose()
  }
  map.on('singleclick', (event) => {
    // While a measure mode is active, clicks belong to the sketch (P1.2) —
    // GetFeatureInfo stays out of the way, including responses that were
    // still in flight when measuring started.
    if (measure.isActive()) return
    const seq = ++selectionSeq
    details.showLoading()
    highlight.showPoint(event.coordinate)
    queryFeatures(map, wmsLayers, event.coordinate, vector)
      .then((hits) => {
        if (seq !== selectionSeq || measure.isActive()) return
        details.showHits(hits)
      })
      .catch((err) => {
        if (seq !== selectionSeq) return
        if (err instanceof ApiError) {
          details.hide()
          highlight.clear()
          handleEnforcement(err)
        } else if (!measure.isActive()) {
          details.showError() // keep the marker — it anchors the error to the click
        } else {
          details.hide()
          highlight.clear()
        }
      })
  })

  disposeMap = () => map.setTarget(undefined)
  // Tile freshness: re-check the layer versions every 30 min and when the tab
  // comes back; the boot fetch above counts as the first one. Skipped when
  // teardown() ran while this screen was still loading (same guard as the map).
  if (gen === screenGen) {
    if (!versions) {
      // Late (or failed) at boot: apply it if it arrives while this screen is still the live one.
      void versionsCall.then((late) => {
        if (late && gen === screenGen) applyVersions(wmsLayers, late)
      })
    }
    disposeVersionRefresh = startVersionRefresh(wmsLayers, {
      fetcher: fetchLayerVersions,
      fetchedAt: versionsStartedAt,
    })
  }
  mapReady = true
}

/** Map ApiError kinds to the enforcement-aware UI states (PRD P0.7). */
function handleEnforcement(err: ApiError): void {
  addLogEntry(err.kind, err.status, err.message)
  const code = errorCode(err.kind, err.status)
  switch (err.kind) {
    case 'auth':
      showLogin('Session expired.')
      break
    case 'seat':
      showSeatBanner(err.message, () => {
        // Tiles refresh via the gate; if the map never came up (seat 429 on
        // capabilities), rebuild the whole screen.
        if (!mapReady && currentMe) void startMapScreen(currentMe)
      }, code)
      break
    case 'subscription':
      teardown()
      renderSubscriptionInactive(app, () => void signOut(), code)
      break
    case 'plan':
      teardown()
      renderNotInPlan(app, () => void signOut(), code)
      break
    case 'unavailable':
      showServiceToast(err.retryAfterSec, () => {
        if (mapReady) gate.refreshAll()
        else if (currentMe) void startMapScreen(currentMe)
      }, code)
      break
    case 'ratelimit':
      // Edge rate-limit (Cloudflare/nginx) — body is empty, not a proxy seat error.
      // By the time this fires the tile loader (map/layers.ts) has already retried
      // that tile three times with backoff: a transient 429 never gets here, only
      // one that persisted. OpenLayers itself never re-requests a failed tile, so
      // it stays failed until the tile cache drops it or the source is refreshed;
      // the map stays interactive.
      // Still do NOT call gate.refreshAll(): refiring every tile into a rate-limit
      // that is still in force only makes it worse.
      console.warn('Tile rate-limited by edge (429, non-seat) — gave up after retries')
      break
    case 'layer':
    case 'service':
      // Capabilities are filtered and WFS UI is gated on allows_wfs, so these
      // should not occur; never invent UI rules (vector mode reverts locally).
      console.warn('Proxy refused the request:', err.message)
      break
    default:
      console.error('Unexpected proxy error:', err.status, err.message)
  }
}

async function signOut(): Promise<void> {
  await logout()
  currentMe = null
  showLogin()
}

function showLogin(notice = ''): void {
  teardown()
  renderLoginScreen(app, () => void boot(), notice)
}

function showLoading(): void {
  app.innerHTML = `<div class="screen"><div class="loading">Loading…</div></div>`
}

function showBootError(err: unknown): void {
  const message =
    err instanceof ApiError ? err.message : 'Cannot reach the map service. Check your connection.'
  app.innerHTML = `
    <div class="screen screen--state">
      <div class="card state-card">
        <h1>Service temporarily unavailable</h1>
        <p>${escapeHtml(message)}</p>
        <button class="btn btn--primary">Retry</button>
      </div>
    </div>`
  app.querySelector('button')!.addEventListener('click', () => void boot())
}

function teardown(): void {
  screenGen += 1
  hideSeatBanner()
  hideServiceToast()
  hideAttributesDrawer()
  disposeSqlPanel?.()
  disposeSqlPanel = null
  disposeVersionRefresh?.()
  disposeVersionRefresh = null
  disposeMap?.()
  disposeMap = null
  mapReady = false
  gate.reset()
}
